import { useState } from 'react';
import { CalendarDate, type DateValue } from '@internationalized/date';
import { Button, DatePicker as AriaDatePicker, Group, I18nProvider } from 'react-aria-components';
import { CalendarDays, X } from 'lucide-react';
import { useT } from '../../../i18n/hooks';
import { DateCalendarPopover, DateSegments, boundText } from './dateParts';
import { FM } from './messages';
import { FieldFoot, FieldLabel, fieldClass, labelText } from './parts';
import { parseIso, todayIso, useCalendarLocale } from './locale';
import type { DatePickerProps } from './types';
import './styles';

type Rejected = 'range' | 'unavailable' | null;

/**
 * Одна дата: сегменты для набора с клавиатуры, значок календаря, очистка.
 * Наружу — ISO `ГГГГ-ММ-ДД` или `null`; календарь с понедельника, язык —
 * языка интерфейса (`calendarLocale`).
 *
 * Ввод вне `minValue`/`maxValue` (или недоступная дата) НЕ уходит наружу:
 * поле само становится невалидным и называет границы, а `onChange` не
 * вызывается — страница не узнаёт о значении, которое сама запретила.
 */
export function DatePicker({
  label,
  'aria-label': ariaLabel,
  explain,
  description,
  errorMessage,
  isInvalid,
  isRequired,
  isDisabled,
  size = 'md',
  className,
  value,
  onChange,
  minValue,
  maxValue,
  isClearable = false,
  placeholderValue,
  isDateUnavailable,
}: DatePickerProps) {
  const t = useT();
  const locale = useCalendarLocale();
  const min = parseIso(minValue);
  const max = parseIso(maxValue);
  const parsed = parseIso(value);

  // Набранное, но не принятое значение живёт здесь, пока `value` снаружи тот же:
  // без этого поле показывало бы старую дату, а человек видел бы, что «не вышло»,
  // не понимая почему. Сброс — когда `value` сменился (страница приняла новое).
  const [seen, setSeen] = useState(value);
  const [draft, setDraft] = useState<DateValue | null>(parsed);
  const [rejected, setRejected] = useState<Rejected>(null);
  if (seen !== value) {
    setSeen(value);
    setDraft(parsed);
    setRejected(null);
  }

  const handle = (next: DateValue | null) => {
    if (next) {
      const outside = (min && next.compare(min) < 0) || (max && next.compare(max) > 0);
      const blocked = !outside && isDateUnavailable?.(next.toString());
      if (outside || blocked) {
        setDraft(next);
        setRejected(outside ? 'range' : 'unavailable');
        return;
      }
    }
    setDraft(next);
    setRejected(null);
    onChange(next ? next.toString() : null);
  };

  let message = errorMessage;
  if (rejected === 'unavailable') message = t(FM.dateUnavailable);
  else if (rejected === 'range') {
    message = min && max ? t(FM.dateOutOfRange, { min: boundText(minValue!), max: boundText(maxValue!) }) : min ? t(FM.dateNotBefore, { min: boundText(minValue!) }) : t(FM.dateNotAfter, { max: boundText(maxValue!) });
  }
  const invalid = Boolean(isInvalid) || rejected !== null;

  // Пустой календарь открывается на ближайшем допустимом месяце, а не на «сегодня» вне окна.
  const today = todayIso();
  const placeholder = parseIso(placeholderValue) ?? (max && today > maxValue! ? max : min && today < minValue! ? min : undefined);

  return (
    <I18nProvider locale={locale}>
      <AriaDatePicker
        className={fieldClass(size, className, 'ac-date')}
        value={draft as CalendarDate | null}
        onChange={handle}
        minValue={min ?? undefined}
        maxValue={max ?? undefined}
        placeholderValue={placeholder}
        isDateUnavailable={isDateUnavailable ? (date) => isDateUnavailable(date.toString()) : undefined}
        isDisabled={isDisabled}
        isInvalid={invalid}
        isRequired={isRequired}
        validationBehavior="aria"
        granularity="day"
        firstDayOfWeek="mon"
        shouldForceLeadingZeros
        aria-label={label ? undefined : ariaLabel}
      >
        <FieldLabel label={label} ariaLabel={ariaLabel} explain={explain} isRequired={isRequired} />
        <Group className="ac-trigger">
          <DateSegments />
          {isClearable && draft ? (
            <Button
              className="fm-date-clear"
              aria-label={t(FM.dateClear)}
              // Крестик внутри поля — отдельное действие, а не частный случай открытия календаря.
              slot={null}
              onPress={() => {
                setDraft(null);
                setRejected(null);
                onChange(null);
              }}
            >
              <X size={14} strokeWidth={2} aria-hidden />
            </Button>
          ) : null}
          <Button aria-label={t(FM.dateOpen)}>
            <CalendarDays size={17} />
          </Button>
        </Group>
        <FieldFoot description={description} errorMessage={message} isInvalid={invalid} />
        <DateCalendarPopover ariaLabel={t(FM.datePick, { field: labelText(label, ariaLabel, t(FM.dateFallback)) })} />
      </AriaDatePicker>
    </I18nProvider>
  );
}
